import React from 'react';
import { FeedItemClassificationDto } from '@/shared/types/api';
import { getClassificationMethodMeta } from '@/shared/lib/classificationMeta';
import { formatDateTime } from '@/shared/utils/date';
import { cn } from '@/shared/utils/cn';

interface ClassificationBadgeProps {
  classification?: FeedItemClassificationDto | null;
  /**
   * The item's LastVersionedAt (SC-278). When the content was revised after it was classified,
   * the verdict below describes text that no longer exists, so the chip says so.
   */
  lastVersionedAt?: string | null;
}

const parseTime = (value?: string | null): number | null => {
  if (!value) return null;
  const ms = new Date(value).getTime();
  return Number.isNaN(ms) ? null : ms;
};

/**
 * Provenance for the Impact/Complexity badges (RF-91): which tier classified the item, how
 * confident it was, what that tier's confidence actually measures, when it decided, and why.
 *
 * Deliberately a neutral .chip-tag rather than a colored one: Impact already owns the hue heat
 * scale and Complexity the monochrome glyph, so a third color channel here would compete with
 * the very badges this is meant to qualify.
 *
 * Accessibility: the rationale lives in the DOM in a visually-hidden span, not only in
 * `title`/`aria-label`. A bare <span> is role=generic, which ARIA forbids naming, so browsers
 * ignore aria-label on it and a screen-reader user would otherwise hear only "LLM 82%".
 * `title` still carries the same text for the mouse-hover tooltip.
 */
export const ClassificationBadge: React.FC<ClassificationBadgeProps> = ({
  classification,
  lastVersionedAt,
}) => {
  if (!classification) return null;

  const meta = getClassificationMethodMeta(classification.method);
  const Icon = meta.icon;
  const percent = Math.round((classification.confidence ?? 0) * 100);
  const reasoning = classification.reasoning?.trim();

  const classifiedAtMs = parseTime(classification.classifiedAt);
  const revisedAtMs = parseTime(lastVersionedAt);
  // Nothing re-classifies an already-classified item (ClassificationJob skips it) and neither
  // Revise() nor SetManualTags() clears the metadata, so a verdict older than the last revision
  // describes content the reader is no longer looking at.
  const isStale = classifiedAtMs !== null && revisedAtMs !== null && revisedAtMs > classifiedAtMs;

  const parts = [
    `Classified by ${meta.label}`,
    `${percent}% confidence (${meta.confidenceMeaning})`,
  ];
  if (classifiedAtMs !== null) parts.push(`on ${formatDateTime(classification.classifiedAt)}`);
  if (reasoning) parts.push(reasoning);
  if (isStale) {
    parts.push(
      `outdated — the item was revised on ${formatDateTime(lastVersionedAt!)}, after it was classified`
    );
  }
  const description = parts.join(' — ');

  const visibleLabel = `${meta.label} ${percent}%${isStale ? ' (outdated)' : ''}`;

  return (
    <span
      className={cn('chip-tag', isStale && 'italic opacity-70')}
      title={description}
      data-stale={isStale ? 'true' : undefined}
    >
      <Icon className="h-3 w-3 shrink-0" aria-hidden="true" />
      {/* Hidden from the a11y tree so the full description below is not read twice. */}
      <span aria-hidden="true">{visibleLabel}</span>
      <span className="sr-only">{description}</span>
    </span>
  );
};
